import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Link,
  Divider
} from '@mui/material';
import {
  MDBox,
  MDTypography,
  MDInput,
  MDButton,
  FaIcon,
  CloseButton
} from '@components/index';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  CLICKED_KNOW_MORE,
  SHARE_CHART,
  SHARE_CHART_LINK_COPIED,
  SHARE_PROJECT,
  SHARE_PROJECT_LINK_COPIED
} from '@constants/eventNames';
import useToast from '@hooks/useToast';
import { validateEmails } from '@utils/common';
import { useUpdateEmailChartLinkMutation } from '@api/chart';
import { useUpdateEmailProjectLinkMutation } from '@api/projects';
import { RootState } from '@store/store';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import { setUserNotifications } from '@components/Notification/slice';
import dayjs from 'dayjs';

interface Props {
  open: boolean;
  onClose: () => void;
  onClickGetLink: () => void;
  chartLink?: string;
  isProject?: boolean;
  chartId?: string;
}

const ShareChartModal: FC<Props> = ({
  open,
  onClose,
  onClickGetLink,
  isProject = false,
  chartId
}) => {
  const projectId = useSelector(
    (state: RootState) => state?.projectBoard.currentProjectData?.id || ''
  );

  const projectName = useSelector(
    (state: RootState) =>
      state?.projectBoard.currentProjectData?.project_name || ''
  );

  const { pushSuccessToast } = useToast();

  const [updateEmailChartLink] = useUpdateEmailChartLinkMutation();
  const [updateEmailProjectLink] = useUpdateEmailProjectLinkMutation();
  const dispatch = useDispatch();

  const [emailsToShare, setEmailsToShare] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  useEffect(() => {
    if (!open) {
      setEmailsToShare([]);
      setEmailError(false);
      setErrorText('');
    }
  }, [open]);

  const handleGetLink = () => {
    onClickGetLink();
    pushSuccessToast('Link copied to clipboard successfully.');
    sendAmplitudeData(
      isProject ? SHARE_PROJECT_LINK_COPIED : SHARE_CHART_LINK_COPIED
    );
    onClose();
  };

  const sendNotification = (msg_event: MSG_EVENT, message: string) => {
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: true,
      is_user_message: true,
      message_event: msg_event,
      message_text: message,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.SUCCESS,
      message_data: JSON.stringify({
        project: { id: projectId, name: projectName }
      }),
      linkKeyword: 'here.',
      replaceKeyword: '<click here>',
      is_important: false,
      recipient_obj: 0,
      isAnchorElement: true
    };

    dispatch(setUserNotifications(payload));
  };

  const handleShareViaEmail = () => {
    const validEmails = validateEmails(emailsToShare);

    if (emailsToShare.length === 0) {
      setEmailError(true);
      setErrorText('Please provide an e-mail address');
    } else if (validEmails.length !== emailsToShare.length) {
      setEmailError(true);
      setErrorText('Please enter e-mail address in correct format.');
    } else if (!emailError) {
      if (isProject) {
        sendAmplitudeData(SHARE_PROJECT, {
          Mode: 'Email',
          Email: emailsToShare
        });
        updateEmailProjectLink({ id: projectId, emailIds: emailsToShare });
      } else {
        sendAmplitudeData(SHARE_CHART, {
          Mode: 'Email',
          Email: emailsToShare
        });
        updateEmailChartLink({ id: chartId, emailIds: emailsToShare });
      }

      sendNotification(
        MSG_EVENT.LINK_SHARED_VIA_EMAIL,
        `An e-mail is sent to the ${validEmails?.join(
          ', '
        )} with the link to this ${
          isProject ? 'Dashboard' : 'Chart'
        }. They can view or leave a comment on it.`
      );
      // sendAmplitudeData(
      //   isProject ? SHARE_PROJECT_LINK_COPIED : SHARE_CHART_LINK_COPIED
      // );
      onClose();
    }
  };

  const handleChangeEmailIds = (value: string) => {
    if (value) {
      const emailArray = value.replaceAll(/\s/g, '').split(',');

      setEmailsToShare(emailArray);

      if (emailArray.length > 5) {
        setEmailError(true);
        setErrorText('Max. 5 e-mail addresses allowed.');
      } else {
        setEmailError(false);
        setErrorText('');
      }
    } else {
      setEmailsToShare([]);
      setEmailError(false);
      setErrorText('');
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      onClose={onClose}
      PaperProps={{ sx: { p: 1 } }}
      sx={{
        zIndex: 80,
        '& .MuiPaper-root': {
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      <CloseButton onClose={() => onClose()} />
      <DialogTitle {...({ component: 'div' } as any)}>
        <MDTypography variant='h3' color='secondary' textGradient mb={1}>
          Share this!
        </MDTypography>
        <MDTypography variant='h5' color='info'>
          Share your work to get quick feedback.
        </MDTypography>
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'visible' }}>
        <MDBox display={'flex'} flexDirection={'column'} rowGap={1} mb={3}>
          <MDTypography variant='h6' color='dark' fontWeight='bold'>
            Access level
          </MDTypography>
          <Select
            sx={{ height: '45px', color: 'red' }}
            defaultValue='view-comment'
            color='info'
          >
            <MenuItem value='view-comment'>View and Comment</MenuItem>
            <MenuItem value='view' disabled>
              View Only (Coming Soon)
            </MenuItem>
            <MenuItem value='edit' disabled>
              Edit (Coming Soon)
            </MenuItem>
          </Select>
        </MDBox>
        <MDBox display={'flex'} flexDirection={'column'} rowGap={1} mb={3}>
          <MDTypography variant='h6' color='dark' fontWeight='bold'>
            Share via
          </MDTypography>
          <Select sx={{ height: '45px' }} color='info' defaultValue='email'>
            <MenuItem value='email'>Email</MenuItem>
            <MenuItem value='telegram' disabled>
              Telegram (Coming Soon)
            </MenuItem>
          </Select>
        </MDBox>
        <MDBox display={'flex'} flexDirection={'column'} rowGap={1} mb={3}>
          <MDTypography variant='h6' color='dark' fontWeight='bold'>
            Enter e-mail address (max. of 5)
          </MDTypography>
          <MDInput
            placeholder='To share to more than 1 e-mail, use comma to separate the e-mail addresses.'
            rows={2}
            multiline
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChangeEmailIds(e.target.value)
            }
            value={emailsToShare}
            error={emailError}
            helperText={emailError && errorText}
          />
        </MDBox>
        <MDBox display='flex' width='100%' justifyContent='center'>
          <MDButton
            size='large'
            color='secondary'
            variant='outlined'
            sx={{ mx: 1 }}
            onClick={() => onClose()}
          >
            Cancel
          </MDButton>
          <MDButton
            variant='gradient'
            color='primary'
            size='large'
            sx={{ mx: 1 }}
            onClick={() => handleShareViaEmail()}
          >
            Share
          </MDButton>
        </MDBox>
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{ flexDirection: 'column', rowGap: 3, mb: 3 }}
        disableSpacing
      >
        <MDBox display='flex' flexDirection='column' width='100%'>
          <MDTypography variant='h6' color='dark' fontWeight='bold'>
            Or copy the link below and share it with anyone
          </MDTypography>
          <MDBox display='flex' justifyContent='center' marginY={3}>
            <MDButton
              size='medium'
              color='light'
              onClick={() => handleGetLink()}
            >
              <FaIcon
                icon='link'
                typographyProps={{
                  color: 'primary',
                  variant: 'xl'
                }}
              />
              <MDTypography
                color='info'
                textTransform='capitalize'
                variant='sm'
                fontWeight='bold'
                pl={1}
              >
                LINK
              </MDTypography>
            </MDButton>
          </MDBox>
        </MDBox>
        <span
          role='presentation'
          onClick={() =>
            sendAmplitudeData(CLICKED_KNOW_MORE, { 'Feature Name': 'Sharing' })
          }
        >
          <MDTypography
            sx={{ cursor: 'pointer', fontWeight: 400, textAlign: 'center' }}
            variant='sm'
            color='secondary'
            textGradient
            component={Link}
            underline
            href='https://datasquirrel.notion.site/More-about-sharing-9b70dcc58e214b01834a78afc6aabfd4'
            target='_blank'
            rel='noreferrer'
            fontWeight='medium'
          >
            Want to know more about sharing?
          </MDTypography>
        </span>
        <span
          role='presentation'
          onClick={() => sendAmplitudeData(CLICKED_KNOW_MORE)}
        >
          <MDTypography
            sx={{
              cursor: 'pointer',
              fontWeight: 400
            }}
            variant='xs'
            color='secondary'
            textGradient
            href='https://datasquirrel.notion.site/Terms-Conditions-1b41ca5daffe4d418770998ddf0e4fa7'
            target='_blank'
            rel='noreferrer'
            component={Link}
            underline
            fontWeight='medium'
          >
            Terms and Conditions
          </MDTypography>
        </span>
      </DialogActions>
    </Dialog>
  );
};

export default ShareChartModal;
